import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Calendar,
  Filter,
  Settings,
  Plus,
  X,
  Clock,
  Image,
  Video,
  Link,
  Sparkles,
  Shuffle,
} from 'lucide-react';
import { PlatformIcon } from '../common/PlatformIcon';
import { Button } from '../ui/button';
import {
  GET_AUTOMATION_RULES,
  GET_SOCIAL_PROFILES,
  CREATE_AUTOMATION_RULE,
  UPDATE_AUTOMATION_RULE,
  DELETE_AUTOMATION_RULE,
  CREATE_POSTING_SCHEDULE_PREFERENCE,
  UPDATE_POSTING_SCHEDULE_PREFERENCE,
  CREATE_POSTING_TIME_WINDOW,
  UPDATE_POSTING_TIME_WINDOW,
  CREATE_POSTING_CONTENT_AGE,
  UPDATE_POSTING_CONTENT_AGE,
} from '../../graphql';
import {
  AutomationRule,
  PostingContentAge,
  PostingSchedulePreference,
  PostingTimeWindow,
  Platform,
} from '../../types';
import { GetAutomationRulesResponse, GetSocialProfilesResponse } from '../../types/graphql';

interface AutomationRulesV2Props {
  accountId: string;
  onRefetch: () => void;
  selectedProfiles?: Set<string>;
}

interface AutomationRuleFormData {
  name: string;
  socialProfileId: string;
  isActive: boolean;
  // Schedule - Shift-based
  shifts: Array<{
    id?: string; // For editing existing shifts
    startTime: string; // "09:00"
    endTime: string; // "17:00"
    spansMidnight: boolean; // true if shift goes past midnight
    daysOfWeek: number[]; // Which days this shift applies to (0=Sunday, 1=Monday, etc.)
  }>;
  scheduleType: 'specific_times' | 'interval' | 'quarter_hours';
  specificTimes: string[]; // ["09:00", "12:00", "15:00"]
  intervalMinutes: number; // for interval mode
  quarterHourMarks: number[]; // [0, 15, 30, 45] - which quarter-hour marks to use
  // Content filters
  includeKeywords: string[];
  excludeKeywords: string[];
  mediaTypes: string[]; // ['bookmark', 'video', 'photo']
  // Content age
  contentAgeMode: 'immediate' | 'within_hours' | 'delay_hours';
  contentAgeHours: number;
  delayHours: number;
  // Post type
  postTypeStrategy: string;
  // Priority
  priority: 'high' | 'medium' | 'low';
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

const WEEKDAYS = [1, 2, 3, 4, 5]; // Monday-Friday

// Mapping: which post types can be created from which media types
const MEDIA_TYPE_TO_POST_TYPE_MAP: Record<string, string[]> = {
  bookmark: ['photo', 'post', 'random'], // Bookmark can make Photo, Link Post, and Random
  video: ['video'], // Video can only make Video
  photo: ['photo'], // Photo can only make Photo
};

// Helper function to get allowed post types based on selected media types
function getAllowedPostTypes(mediaTypes: string[]): string[] {
  if (mediaTypes.length === 0) {
    // If no media types selected, all post types are allowed
    return ['auto', 'photo', 'video', 'post', 'random'];
  }

  // Get allowed post types for each selected media type
  const allowedSets = mediaTypes.map((mediaType) => {
    return MEDIA_TYPE_TO_POST_TYPE_MAP[mediaType] || [];
  });

  // Find intersection - post types that are allowed for ALL selected media types
  if (allowedSets.length === 0) return [];

  const intersection = allowedSets.reduce((acc, current) => {
    return acc.filter((postType) => current.includes(postType));
  });

  // Always include 'auto' as it can work with any media type
  const result = ['auto', ...intersection];

  // 'random' is only available for bookmark
  if (mediaTypes.includes('bookmark')) {
    result.push('random');
  }

  return result;
}

// Helper function to check if a post type is valid for selected media types
function isPostTypeValid(postType: string, mediaTypes: string[]): boolean {
  const allowedTypes = getAllowedPostTypes(mediaTypes);
  return allowedTypes.includes(postType);
}

// Helper function to convert form data to legacy schema
function convertFormToLegacySchema(
  formData: AutomationRuleFormData,
  ruleId: string
): {
  timeWindows: Partial<PostingTimeWindow>[];
  schedulePreferences: Partial<PostingSchedulePreference>[];
  contentAge: Partial<PostingContentAge>;
  dbFilterRule: string;
  priority: number;
} {
  // Convert shifts to time windows
  // Each shift creates a time window for each day it applies to
  const timeWindows: Partial<PostingTimeWindow>[] = [];
  for (const shift of formData.shifts) {
    for (const dayOfWeek of shift.daysOfWeek) {
      timeWindows.push({
        postingRuleId: ruleId,
        dayOfWeek,
        startTime: shift.startTime,
        endTime: shift.endTime,
        spansMidnight: shift.spansMidnight || undefined,
        isActive: true,
      });
    }
  }

  // Convert schedule preferences based on schedule type
  const schedulePreferences: Partial<PostingSchedulePreference>[] =
    formData.scheduleType === 'quarter_hours'
      ? // For quarter-hour marks, create one preference per selected mark (0, 15, 30, 45)
        formData.quarterHourMarks.map((minuteMark) => ({
          postingRuleId: ruleId,
          minuteMark,
          intervalMinutes: 60, // Every hour at this minute mark
          isActive: true,
        }))
      : formData.scheduleType === 'specific_times'
        ? // For specific times, create one preference per time
          formData.specificTimes.map((time) => {
            const [hours, minutes] = time.split(':').map(Number);
            const totalMinutes = hours * 60 + minutes;
            return {
              postingRuleId: ruleId,
              minuteMark: totalMinutes,
              intervalMinutes: 1440, // 24 hours - ensures only one slot per day per preference
              isActive: true,
            };
          })
        : // For interval mode
          [
            {
              postingRuleId: ruleId,
              minuteMark: 0, // Start at top of hour
              intervalMinutes: formData.intervalMinutes,
              isActive: true,
            },
          ];

  // Convert content age
  const contentAge: Partial<PostingContentAge> = {
    postingRuleId: ruleId,
    isImmediate: formData.contentAgeMode === 'immediate',
    maxAgeHours: formData.contentAgeMode === 'within_hours' ? formData.contentAgeHours : undefined,
    // Always set delayOffsetHours from formData.delayHours (use nullish coalescing to allow 0)
    delayOffsetHours: formData.delayHours ?? 0,
    minAgeHours: 0,
    isActive: true,
  };

  // Convert keywords to SQL filter
  // Search in: t.title, t.destination_url, t.keywords, t.category
  // Use ILIKE for case-insensitive matching (more efficient than LOWER(...) LIKE)
  let dbFilterRule = '';

  // Add media type filter if media types are selected
  if (formData.mediaTypes.length > 0) {
    // Escape single quotes in media type values to prevent SQL injection
    // Convert to uppercase for SQL filter
    const escapedMediaTypes = formData.mediaTypes
      .map((type) => `'${type.toUpperCase().replace(/'/g, "''")}'`)
      .join(',');
    dbFilterRule += `t.media_type IN (${escapedMediaTypes})`;
  }

  if (formData.includeKeywords.length > 0) {
    const includeConditions = formData.includeKeywords
      .map((keyword) => {
        // Escape single quotes in keyword to prevent SQL injection
        const escapedKeyword = keyword.replace(/'/g, "''");
        return `(t.title ILIKE '%${escapedKeyword}%' OR t.destination_url ILIKE '%${escapedKeyword}%' OR t.keywords ILIKE '%${escapedKeyword}%' OR t.category ILIKE '%${escapedKeyword}%')`;
      })
      .join(' OR ');
    if (dbFilterRule) dbFilterRule += ' AND ';
    dbFilterRule += `(${includeConditions})`;
  }
  if (formData.excludeKeywords.length > 0) {
    const excludeConditions = formData.excludeKeywords
      .map((keyword) => {
        // Escape single quotes in keyword to prevent SQL injection
        const escapedKeyword = keyword.replace(/'/g, "''");
        // For exclude, keyword must not be in any of the fields
        return `(t.title NOT ILIKE '%${escapedKeyword}%' AND t.destination_url NOT ILIKE '%${escapedKeyword}%' AND t.keywords NOT ILIKE '%${escapedKeyword}%' AND t.category NOT ILIKE '%${escapedKeyword}%')`;
      })
      .join(' AND ');
    if (dbFilterRule) dbFilterRule += ' AND ';
    dbFilterRule += excludeConditions;
  }

  // Convert priority
  const priorityMap = { high: 3, medium: 2, low: 1 };
  const priority = priorityMap[formData.priority];

  return {
    timeWindows,
    schedulePreferences,
    contentAge,
    dbFilterRule,
    priority,
  };
}

// Helper functions for revision tracking
// Using localStorage to track revisions until backend support is added
const REVISION_STORAGE_KEY = 'automation_rule_revisions';

function getRevisionForRule(ruleId: string): number {
  try {
    const stored = localStorage.getItem(REVISION_STORAGE_KEY);
    if (!stored) return 1;
    const revisions: Record<string, number> = JSON.parse(stored);
    return revisions[ruleId] || 1;
  } catch {
    return 1;
  }
}

function setRevisionForRule(ruleId: string, revision: number): void {
  try {
    const stored = localStorage.getItem(REVISION_STORAGE_KEY);
    const revisions: Record<string, number> = stored ? JSON.parse(stored) : {};
    revisions[ruleId] = revision;
    localStorage.setItem(REVISION_STORAGE_KEY, JSON.stringify(revisions));
  } catch (error) {
    console.error('Failed to store revision:', error);
  }
}

function incrementRevisionForRule(ruleId: string): number {
  const current = getRevisionForRule(ruleId);
  const newRevision = current + 1;
  setRevisionForRule(ruleId, newRevision);
  return newRevision;
}

// Helper function to convert existing rule to form data
function convertRuleToFormData(rule: AutomationRule): AutomationRuleFormData {
  // Convert time windows to shifts
  const shifts: Array<{
    id?: string;
    startTime: string;
    endTime: string;
    spansMidnight: boolean;
    daysOfWeek: number[];
  }> = [];

  if (rule.postingTimeWindows && rule.postingTimeWindows.length > 0) {
    // Group time windows by startTime/endTime/spansMidnight
    // Only include ACTIVE windows when populating the form
    const shiftMap = new Map<
      string,
      { startTime: string; endTime: string; spansMidnight: boolean; daysOfWeek: Set<number> }
    >();

    console.log('Converting time windows, total count:', rule.postingTimeWindows.length);
    for (const window of rule.postingTimeWindows) {
      console.log('Time window:', {
        id: window.id,
        isActive: window.isActive,
        startTime: window.startTime,
        endTime: window.endTime,
        dayOfWeek: window.dayOfWeek,
      });
      // Only process active windows for the form
      if (!window.isActive) {
        console.log('Skipping inactive window:', window.id);
        continue;
      }
      const key = `${window.startTime}-${window.endTime}-${window.spansMidnight || false}`;

      if (!shiftMap.has(key)) {
        shiftMap.set(key, {
          startTime: window.startTime,
          endTime: window.endTime,
          spansMidnight: window.spansMidnight || false,
          daysOfWeek: new Set(),
        });
      }
      shiftMap.get(key)!.daysOfWeek.add(window.dayOfWeek);
    }

    console.log('Shift map size:', shiftMap.size);
    // Convert map to array
    //eslint-disable-next-line
    for (const [_, shift] of shiftMap) {
      shifts.push({
        startTime: shift.startTime,
        endTime: shift.endTime,
        spansMidnight: shift.spansMidnight,
        daysOfWeek: Array.from(shift.daysOfWeek).sort(),
      });
    }
    console.log('Final shifts:', shifts);
  }

  // Default to one shift if none found
  // This should only happen if there are no active time windows
  if (shifts.length === 0) {
    console.warn(
      'No active time windows found, using default shift. Total windows:',
      rule.postingTimeWindows?.length || 0
    );
    if (rule.postingTimeWindows && rule.postingTimeWindows.length > 0) {
      // If we have windows but they're all inactive, log them for debugging
      console.warn(
        'All time windows are inactive:',
        rule.postingTimeWindows.map((w) => ({
          id: w.id,
          isActive: w.isActive,
          startTime: w.startTime,
          endTime: w.endTime,
        }))
      );
    }
    shifts.push({
      startTime: '09:00',
      endTime: '17:00',
      spansMidnight: false,
      daysOfWeek: WEEKDAYS,
    });
  }

  // Determine schedule type from schedule preferences
  let scheduleType: 'specific_times' | 'interval' | 'quarter_hours' = 'specific_times';
  let specificTimes: string[] = [];
  let intervalMinutes = 120;
  let quarterHourMarks: number[] = [];

  if (rule.postingSchedulePreferences && rule.postingSchedulePreferences.length > 0) {
    const prefs = rule.postingSchedulePreferences.filter((p) => p.isActive);

    if (prefs.length > 0) {
      // Check if all preferences have intervalMinutes === 1440 (specific times)
      const allSpecificTimes = prefs.every((p) => p.intervalMinutes === 1440);
      // Check if all preferences have intervalMinutes === 60 and minuteMark in [0,15,30,45] (quarter hours)
      const allQuarterHours = prefs.every(
        (p) => p.intervalMinutes === 60 && [0, 15, 30, 45].includes(p.minuteMark)
      );

      if (allSpecificTimes) {
        scheduleType = 'specific_times';
        // Extract times from minuteMark (minuteMark stores total minutes since midnight)
        specificTimes = prefs
          .map((p) => {
            const hours = Math.floor(p.minuteMark / 60);
            const minutes = p.minuteMark % 60;
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          })
          .sort();
      } else if (allQuarterHours) {
        scheduleType = 'quarter_hours';
        quarterHourMarks = prefs
          .map((p) => p.minuteMark)
          .filter((m) => [0, 15, 30, 45].includes(m));
        if (quarterHourMarks.length === 0) {
          quarterHourMarks = [0, 15, 30, 45];
        }
      } else {
        scheduleType = 'interval';
        // Use the first preference's intervalMinutes
        intervalMinutes = prefs[0].intervalMinutes || 120;
      }
    }
  }

  // Parse keywords from dbFilterRule
  const includeKeywords: string[] = [];
  const excludeKeywords: string[] = [];

  if (rule.dbFilterRule) {
    const includePatterns = [
      /t\.(?:title|destination_url|keywords|category)\s+ILIKE\s+'%([^%]+)%'/gi,
      /LOWER\(t\.(?:title|destination_url|keywords|category)\)\s+LIKE\s+'%([^%]+)%'/gi,
    ];

    const foundIncludeKeywords = new Set<string>();
    includePatterns.forEach((pattern) => {
      const matches = rule.dbFilterRule!.match(pattern);
      if (matches) {
        matches.forEach((match) => {
          const keywordMatch = match.match(/'%([^%]+)%'/i);
          if (keywordMatch && keywordMatch[1]) {
            // Unescape single quotes
            const keyword = keywordMatch[1].replace(/''/g, "'");
            foundIncludeKeywords.add(keyword);
          }
        });
      }
    });
    includeKeywords.push(...Array.from(foundIncludeKeywords));

    const excludePatterns = [
      /t\.(?:title|destination_url|keywords|category)\s+NOT\s+ILIKE\s+'%([^%]+)%'/gi,
      /LOWER\(t\.(?:title|destination_url|keywords|category)\)\s+NOT\s+LIKE\s+'%([^%]+)%'/gi,
    ];

    const foundExcludeKeywords = new Set<string>();
    excludePatterns.forEach((pattern) => {
      const matches = rule.dbFilterRule!.match(pattern);
      if (matches) {
        matches.forEach((match) => {
          const keywordMatch = match.match(/'%([^%]+)%'/i);
          if (keywordMatch && keywordMatch[1]) {
            // Unescape single quotes
            const keyword = keywordMatch[1].replace(/''/g, "'");
            foundExcludeKeywords.add(keyword);
          }
        });
      }
    });
    excludeKeywords.push(...Array.from(foundExcludeKeywords));
  }

  // Parse media types from dbFilterRule
  const mediaTypes: string[] = [];
  if (rule.dbFilterRule) {
    // Match pattern: t.media_type IN ('BOOKMARK', 'VIDEO', 'PHOTO')
    const mediaTypePattern = /t\.media_type\s+IN\s*\(([^)]+)\)/i;
    const match = rule.dbFilterRule.match(mediaTypePattern);
    if (match && match[1]) {
      // Extract quoted values: 'BOOKMARK', 'VIDEO', 'PHOTO'
      const quotedValues = match[1].match(/'([^']+)'/g);
      if (quotedValues) {
        quotedValues.forEach((quoted) => {
          // Remove quotes and convert to lowercase
          const mediaType = quoted.replace(/'/g, '').toLowerCase();
          // Unescape single quotes if any
          const unescaped = mediaType.replace(/''/g, "'");
          if (['bookmark', 'video', 'photo'].includes(unescaped)) {
            mediaTypes.push(unescaped);
          }
        });
      }
    }
  }

  // Convert content age
  let contentAgeMode: 'immediate' | 'within_hours' | 'delay_hours' = 'within_hours';
  let contentAgeHours = 24;
  let delayHours = 0;

  if (rule.postingContentAge && rule.postingContentAge.length > 0) {
    const contentAge =
      rule.postingContentAge.find((ca) => ca.isActive) || rule.postingContentAge[0];

    delayHours = contentAge.delayOffsetHours ?? 0;

    if (contentAge.isImmediate) {
      contentAgeMode = 'immediate';
      if (contentAge.maxAgeHours !== null && contentAge.maxAgeHours !== undefined) {
        contentAgeHours = contentAge.maxAgeHours;
      }
    } else if (contentAge.maxAgeHours !== null && contentAge.maxAgeHours !== undefined) {
      contentAgeMode = 'within_hours';
      contentAgeHours = contentAge.maxAgeHours;
    } else {
      contentAgeMode = 'delay_hours';
    }
  }

  const priorityMap: Record<number, 'high' | 'medium' | 'low'> = {
    3: 'high',
    2: 'medium',
    1: 'low',
  };
  const priority = priorityMap[rule.priority] || 'medium';

  return {
    name: rule.name || '',
    socialProfileId: rule.socialProfileId || '',
    isActive: rule.isActive ?? true,
    shifts,
    scheduleType,
    specificTimes: specificTimes.length > 0 ? specificTimes : ['09:00', '12:00', '15:00'],
    intervalMinutes,
    quarterHourMarks: quarterHourMarks.length > 0 ? quarterHourMarks : [0, 15, 30, 45],
    includeKeywords,
    excludeKeywords,
    mediaTypes, // Parsed from dbFilterRule, empty array means all types
    contentAgeMode,
    contentAgeHours,
    delayHours,
    postTypeStrategy: rule.postTypeStrategy || 'auto',
    priority,
  };
}

export default function AutomationRulesV2({
  accountId,
  onRefetch,
  selectedProfiles = new Set(),
}: AutomationRulesV2Props) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);
  const formPopulatedRef = useRef(false);
  const [formData, setFormData] = useState<AutomationRuleFormData>({
    name: '',
    socialProfileId: '',
    isActive: true,
    shifts: [
      {
        startTime: '09:00',
        endTime: '17:00',
        spansMidnight: false,
        daysOfWeek: WEEKDAYS,
      },
    ],
    scheduleType: 'specific_times',
    specificTimes: ['09:00', '12:00', '15:00'],
    intervalMinutes: 120,
    quarterHourMarks: [0, 15, 30, 45],
    includeKeywords: [],
    excludeKeywords: [],
    mediaTypes: [],
    contentAgeMode: 'within_hours',
    contentAgeHours: 24,
    delayHours: 0,
    postTypeStrategy: 'auto',
    priority: 'medium',
  });

  const {
    data: automationRulesData,
    loading: automationRulesLoading,
    refetch: refetchRules,
  } = useQuery<GetAutomationRulesResponse>(GET_AUTOMATION_RULES, {
    variables: { accountId },
    fetchPolicy: 'cache-and-network',
  });

  const { data: socialProfilesData, loading: socialProfilesLoading } =
    useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
      variables: { accountId },
    });

  const [createAutomationRule] = useMutation(CREATE_AUTOMATION_RULE);
  const [updateAutomationRule] = useMutation(UPDATE_AUTOMATION_RULE);
  const [deleteAutomationRule] = useMutation(DELETE_AUTOMATION_RULE);
  const [createTimeWindow] = useMutation(CREATE_POSTING_TIME_WINDOW);
  const [updateTimeWindow] = useMutation(UPDATE_POSTING_TIME_WINDOW);
  const [createSchedulePreference] = useMutation(CREATE_POSTING_SCHEDULE_PREFERENCE);
  const [updateSchedulePreference] = useMutation(UPDATE_POSTING_SCHEDULE_PREFERENCE);
  const [createContentAge] = useMutation(CREATE_POSTING_CONTENT_AGE);
  const [updateContentAge] = useMutation(UPDATE_POSTING_CONTENT_AGE);

  // Initialize revisions for existing rules when they're loaded
  useEffect(() => {
    if (automationRulesData?.automationRules) {
      automationRulesData.automationRules.forEach((rule) => {
        // Initialize revision to 1 if it doesn't exist in storage
        try {
          const stored = localStorage.getItem(REVISION_STORAGE_KEY);
          const revisions: Record<string, number> = stored ? JSON.parse(stored) : {};
          if (!(rule.id in revisions)) {
            setRevisionForRule(rule.id, 1);
          }
        } catch {
          // If storage fails, silently continue
        }
      });
    }
  }, [automationRulesData]);

  useEffect(() => {
    if (isCreating && editingRule && !formPopulatedRef.current) {
      console.log('useEffect populating form - editingRule:', editingRule);
      const convertedData = convertRuleToFormData(editingRule);
      console.log('useEffect converted data:', convertedData);
      setFormData(convertedData);
      formPopulatedRef.current = true;
    }
    if (!isCreating) {
      formPopulatedRef.current = false;
    }
  }, [editingRule, isCreating]);

  // Validate and reset post type if it becomes invalid when media types change
  useEffect(() => {
    if (
      formData.mediaTypes.length > 0 &&
      !isPostTypeValid(formData.postTypeStrategy, formData.mediaTypes)
    ) {
      // Reset to 'auto' if current post type is not valid for selected media types
      setFormData((prev) => ({ ...prev, postTypeStrategy: 'auto' }));
    }
  }, [formData.mediaTypes, formData.postTypeStrategy]);

  const handleCloseModal = () => {
    setIsCreating(false);
    setEditingRule(null);
    formPopulatedRef.current = false;
  };

  const handleCreateRule = async () => {
    if (!formData.name || !formData.socialProfileId) {
      alert('Please fill in all required fields');
      return;
    }

    const isEditing = !!editingRule;
    const ruleId = isEditing ? editingRule!.id : '';

    try {
      let finalRuleId: string;
      let currentRevision: number;

      if (isEditing) {
        currentRevision = incrementRevisionForRule(ruleId);

        const { dbFilterRule } = convertFormToLegacySchema(formData, ruleId);
        await updateAutomationRule({
          variables: {
            id: ruleId,
            input: {
              name: formData.name,
              priority: formData.priority === 'high' ? 3 : formData.priority === 'medium' ? 2 : 1,
              isActive: formData.isActive,
              postTypeStrategy: formData.postTypeStrategy || 'auto',
              dbFilterRule: dbFilterRule || null,
            },
          },
        });
        finalRuleId = ruleId;

        if (editingRule!.postingTimeWindows && editingRule!.postingTimeWindows.length > 0) {
          const activeWindows = editingRule!.postingTimeWindows.filter((w) => w.isActive);
          const inactiveWindows = editingRule!.postingTimeWindows.filter((w) => !w.isActive);
          console.log('Active windows to deactivate:', activeWindows.length);
          console.log('Inactive windows (will also deactivate):', inactiveWindows.length);

          for (const window of editingRule!.postingTimeWindows) {
            // Deactivate all windows, regardless of current state
            // This ensures we don't have duplicates when creating new windows
            console.log(
              'Deactivating window:',
              window.id,
              'isActive:',
              window.isActive,
              window.startTime,
              window.endTime,
              'dayOfWeek:',
              window.dayOfWeek
            );
            try {
              const result = await updateTimeWindow({
                variables: {
                  id: window.id,
                  input: { isActive: false },
                },
              });
              console.log('Successfully deactivated window:', window.id, result);
            } catch (error) {
              console.error('Error deactivating window:', window.id, error);
            }
          }
        } else {
          console.log('No time windows found in editingRule');
        }

        if (editingRule!.postingSchedulePreferences) {
          for (const pref of editingRule!.postingSchedulePreferences) {
            await updateSchedulePreference({
              variables: {
                id: pref.id,
                input: { isActive: false },
              },
            });
          }
        }

        if (editingRule!.postingContentAge && editingRule!.postingContentAge.length > 0) {
          // Deactivate all content age records
          for (const contentAge of editingRule!.postingContentAge) {
            await updateContentAge({
              variables: {
                id: contentAge.id,
                input: { isActive: false },
              },
            });
          }
        }
      } else {
        // Create the main rule
        const { dbFilterRule } = convertFormToLegacySchema(formData, '');
        const { data } = await createAutomationRule({
          variables: {
            input: {
              accountId,
              socialProfileId: formData.socialProfileId,
              name: formData.name,
              type: 'auto_post',
              priority: formData.priority === 'high' ? 3 : formData.priority === 'medium' ? 2 : 1,
              dbFilterRule: dbFilterRule || null,
              postTypeStrategy: formData.postTypeStrategy || 'auto',
            },
          },
        });
        finalRuleId = data.createAutomationRule.id;
        // Initialize revision to 1 for new rule
        currentRevision = 1;
        setRevisionForRule(finalRuleId, currentRevision);
      }

      // Create new child objects (same for both create and update)
      const { timeWindows, schedulePreferences, contentAge } = convertFormToLegacySchema(
        formData,
        finalRuleId
      );

      // Create time windows
      for (const window of timeWindows) {
        await createTimeWindow({
          variables: { input: window },
        });
      }

      // Create schedule preferences
      for (const pref of schedulePreferences) {
        await createSchedulePreference({
          variables: { input: pref },
        });
      }

      // Create content age
      await createContentAge({
        variables: { input: contentAge },
      });

      // Reset form and refresh
      handleCloseModal();
      setFormData({
        name: '',
        socialProfileId: '',
        isActive: true,
        shifts: [
          {
            startTime: '09:00',
            endTime: '17:00',
            spansMidnight: false,
            daysOfWeek: WEEKDAYS,
          },
        ],
        scheduleType: 'specific_times',
        specificTimes: ['09:00', '12:00', '15:00'],
        intervalMinutes: 120,
        quarterHourMarks: [0, 15, 30, 45],
        includeKeywords: [],
        excludeKeywords: [],
        mediaTypes: [],
        contentAgeMode: 'within_hours',
        contentAgeHours: 24,
        delayHours: 2,
        postTypeStrategy: 'auto',
        priority: 'medium',
      });
      await refetchRules();
      onRefetch();
    } catch (error: any) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} automation rule:`, error);
      alert(`Failed to ${isEditing ? 'update' : 'create'} automation rule: ${error.message}`);
    }
  };

  const handleAddTime = () => {
    setFormData((prev) => ({
      ...prev,
      specificTimes: [...prev.specificTimes, '09:00'],
    }));
  };

  const handleRemoveTime = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      specificTimes: prev.specificTimes.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateTime = (index: number, time: string) => {
    setFormData((prev) => ({
      ...prev,
      specificTimes: prev.specificTimes.map((t, i) => (i === index ? time : t)),
    }));
  };

  const handleAddKeyword = (type: 'include' | 'exclude', keyword: string) => {
    if (!keyword.trim()) return;
    setFormData((prev) => ({
      ...prev,
      [type === 'include' ? 'includeKeywords' : 'excludeKeywords']: [
        ...(type === 'include' ? prev.includeKeywords : prev.excludeKeywords),
        keyword.trim(),
      ],
    }));
  };

  const handleRemoveKeyword = (type: 'include' | 'exclude', index: number) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'include' ? 'includeKeywords' : 'excludeKeywords']: (type === 'include'
        ? prev.includeKeywords
        : prev.excludeKeywords
      ).filter((_, i) => i !== index),
    }));
  };

  if (automationRulesLoading || socialProfilesLoading) {
    return <p className="text-cmp-ink">Loading automations...</p>;
  }

  const socialProfiles = socialProfilesData?.socialProfiles || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cmp-ink mb-2">Automation Rules</h1>
          <p className="text-cmp-ink-2">
            Automatically post content to your social media accounts based on your schedule and
            preferences
          </p>
        </div>
        <Button
          onClick={() => {
            // Reset form and clear editing state when creating new
            formPopulatedRef.current = false; // Reset ref for new creation
            setEditingRule(null);
            setFormData({
              name: '',
              socialProfileId: '',
              isActive: true,
              shifts: [
                {
                  startTime: '09:00',
                  endTime: '17:00',
                  spansMidnight: false,
                  daysOfWeek: WEEKDAYS,
                },
              ],
              scheduleType: 'specific_times',
              specificTimes: ['09:00', '12:00', '15:00'],
              intervalMinutes: 120,
              quarterHourMarks: [0, 15, 30, 45],
              includeKeywords: [],
              excludeKeywords: [],
              mediaTypes: [],
              contentAgeMode: 'within_hours',
              contentAgeHours: 24,
              delayHours: 2,
              postTypeStrategy: 'auto',
              priority: 'medium',
            });
            setIsCreating(true);
          }}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          New Automation
        </Button>
      </div>

      {/* Rules List */}
      <div className="space-y-4">
        {automationRulesData?.automationRules
          .filter((rule) => {
            if (selectedProfiles.size === 0) return true;
            return selectedProfiles.has(rule.socialProfileId);
          })
          .map((rule) => {
            const profile = socialProfiles.find((p) => p.id === rule.socialProfileId);
            return (
              <div
                key={rule.id}
                className="bg-core-dark border border-cmp-line-2 rounded-lg p-6 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {profile && <PlatformIcon platform={profile.platform as Platform} size={24} />}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-cmp-ink">{rule.name}</h3>
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-700 text-cmp-ink">
                          v{getRevisionForRule(rule.id)}
                        </span>
                      </div>
                      {profile && <p className="text-sm text-cmp-ink-2">{profile.profileName}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 rounded text-sm ${
                        rule.isActive
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-cmp-surface/20 text-cmp-ink-2'
                      }`}
                    >
                      {rule.isActive ? 'Active' : 'Paused'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Populate form immediately to avoid race condition
                        console.log('Edit clicked - rule:', rule);
                        console.log('Rule name:', rule.name);
                        console.log('Rule socialProfileId:', rule.socialProfileId);
                        console.log('Rule postingTimeWindows:', rule.postingTimeWindows);
                        console.log(
                          'Rule postingTimeWindows count:',
                          rule.postingTimeWindows?.length || 0
                        );
                        if (rule.postingTimeWindows) {
                          rule.postingTimeWindows.forEach((w, i) => {
                            console.log(`Window ${i}:`, {
                              id: w.id,
                              isActive: w.isActive,
                              startTime: w.startTime,
                              endTime: w.endTime,
                              dayOfWeek: w.dayOfWeek,
                            });
                          });
                        }
                        const convertedData = convertRuleToFormData(rule);
                        console.log('Converted data:', convertedData);
                        console.log('Converted name:', convertedData.name);
                        console.log('Converted socialProfileId:', convertedData.socialProfileId);
                        console.log('Converted shifts:', convertedData.shifts);
                        formPopulatedRef.current = true; // Mark that we've manually populated
                        setFormData(convertedData);
                        setEditingRule(rule);
                        setIsCreating(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (window.confirm('Delete this automation rule?')) {
                          await deleteAutomationRule({ variables: { id: rule.id } });
                          await refetchRules();
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Create/Edit Modal */}
      {isCreating &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50"
            style={{ top: 0, left: 0, right: 0, bottom: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseModal();
              }
            }}
          >
            <div
              className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-4xl w-full max-h-[90vh] overflow-auto m-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-cmp-ink">
                      {editingRule ? 'Edit Automation' : 'Create New Automation'}
                    </h2>
                    {editingRule && (
                      <span className="px-2 py-1 rounded text-sm bg-gray-700 text-cmp-ink">
                        Revision {getRevisionForRule(editingRule.id)} →{' '}
                        {getRevisionForRule(editingRule.id) + 1}
                      </span>
                    )}
                  </div>
                  <Button variant="ghost" onClick={handleCloseModal}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-cmp-ink mb-2">
                      Automation Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Facebook Tech Posts"
                      className="w-full px-4 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-cmp-ink mb-2">
                      Social Account *
                    </label>
                    {editingRule ? (
                      // Read-only display when editing
                      <div className="flex items-center gap-3 p-4 rounded-lg border-2 border-cmp-line-2 bg-form-dark">
                        {(() => {
                          const profile = socialProfiles.find(
                            (p) => p.id === formData.socialProfileId
                          );
                          return profile ? (
                            <>
                              <PlatformIcon platform={profile.platform as Platform} size={32} />
                              <span className="text-sm font-medium text-cmp-ink">
                                {profile.profileName}
                              </span>
                            </>
                          ) : (
                            <span className="text-sm text-cmp-ink-2">Account not found</span>
                          );
                        })()}
                      </div>
                    ) : (
                      // Selectable grid when creating
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {socialProfiles.map((profile) => (
                          <button
                            key={profile.id}
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({ ...prev, socialProfileId: profile.id }))
                            }
                            className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                              formData.socialProfileId === profile.id
                                ? 'border-primary bg-primary/20'
                                : 'border-cmp-line-2 bg-form-dark hover:border-cmp-line-2'
                            }`}
                          >
                            <PlatformIcon platform={profile.platform as Platform} size={32} />
                            <span className="text-xs text-cmp-ink text-center">
                              {profile.profileName}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* What Media to use - Selecting from Media Library */}
                <div className="border-t border-cmp-line-2 pt-6 space-y-1">
                  <div className="flex items-center gap-2 mb-0">
                    <Filter className="w-5 h-5 text-cmp-ink" />
                    <h3 className="text-lg font-semibold text-cmp-ink">What Media to use</h3>
                  </div>
                  <p className="text-sm text-cmp-ink-2 mb-4 mt-1">
                    Select and filter content from your Media Library to use for posts
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-cmp-ink mb-2">
                      Media Types
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((prev) => {
                            const isSelected = prev.mediaTypes.includes('bookmark');
                            return {
                              ...prev,
                              mediaTypes: isSelected
                                ? prev.mediaTypes.filter((t) => t !== 'bookmark')
                                : [...prev.mediaTypes, 'bookmark'],
                            };
                          });
                        }}
                        className={`px-4 py-3 rounded flex items-center justify-center gap-2 font-medium transition-colors ${
                          formData.mediaTypes.includes('bookmark')
                            ? 'bg-primary text-onfill border border-primary hover:bg-primary-medium'
                            : 'bg-form-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                        }`}
                      >
                        <Link className="w-5 h-5" />
                        <span>Bookmark</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((prev) => {
                            const isSelected = prev.mediaTypes.includes('video');
                            return {
                              ...prev,
                              mediaTypes: isSelected
                                ? prev.mediaTypes.filter((t) => t !== 'video')
                                : [...prev.mediaTypes, 'video'],
                            };
                          });
                        }}
                        className={`px-4 py-3 rounded flex items-center justify-center gap-2 font-medium transition-colors ${
                          formData.mediaTypes.includes('video')
                            ? 'bg-primary text-onfill border border-primary hover:bg-primary-medium'
                            : 'bg-form-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                        }`}
                      >
                        <Video className="w-5 h-5" />
                        <span>Video</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((prev) => {
                            const isSelected = prev.mediaTypes.includes('photo');
                            return {
                              ...prev,
                              mediaTypes: isSelected
                                ? prev.mediaTypes.filter((t) => t !== 'photo')
                                : [...prev.mediaTypes, 'photo'],
                            };
                          });
                        }}
                        className={`px-4 py-3 rounded flex items-center justify-center gap-2 font-medium transition-colors ${
                          formData.mediaTypes.includes('photo')
                            ? 'bg-primary text-onfill border border-primary hover:bg-primary-medium'
                            : 'bg-form-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                        }`}
                      >
                        <Image className="w-5 h-5" />
                        <span>Photo</span>
                      </button>
                    </div>
                    <p className="text-xs text-cmp-ink-2 mt-2">
                      Select which types of media to include.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-cmp-ink mb-2">
                      Include Keywords (optional)
                    </label>
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {formData.includeKeywords.map((keyword, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm"
                          >
                            {keyword}
                            <button
                              type="button"
                              onClick={() => handleRemoveKeyword('include', index)}
                              className="hover:text-primary-dark"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter keyword and press Enter"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddKeyword('include', e.currentTarget.value);
                              e.currentTarget.value = '';
                            }
                          }}
                          className="flex-1 px-4 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-cmp-ink mb-2">
                      Exclude Keywords (optional)
                    </label>
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {formData.excludeKeywords.map((keyword, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm"
                          >
                            {keyword}
                            <button
                              type="button"
                              onClick={() => handleRemoveKeyword('exclude', index)}
                              className="hover:text-red-300"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter keyword and press Enter"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddKeyword('exclude', e.currentTarget.value);
                              e.currentTarget.value = '';
                            }
                          }}
                          className="flex-1 px-4 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-cmp-ink mt-4 mb-2">
                      Content Age
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.contentAgeMode === 'immediate'}
                          onChange={() =>
                            setFormData((prev) => ({ ...prev, contentAgeMode: 'immediate' }))
                          }
                          className="w-4 h-4 accent-cmp-bronze"
                        />
                        <span className="text-cmp-ink">
                          Post immediately when content is published
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.contentAgeMode === 'within_hours'}
                          onChange={() =>
                            setFormData((prev) => ({ ...prev, contentAgeMode: 'within_hours' }))
                          }
                          className="w-4 h-4 accent-cmp-bronze"
                        />
                        <span className="text-cmp-ink">Media published within the last</span>
                        <input
                          type="number"
                          min="1"
                          value={formData.contentAgeHours}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              contentAgeHours: parseInt(e.target.value) || 24,
                            }))
                          }
                          disabled={formData.contentAgeMode !== 'within_hours'}
                          className="w-20 px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                        />
                        <span className="text-cmp-ink">hours</span>
                      </label>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-cmp-ink mb-2">
                        Wait after media is published
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          value={formData.delayHours}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              delayHours: parseInt(e.target.value) || 0,
                            }))
                          }
                          className="w-20 px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <span className="text-cmp-ink">hours</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* What will the Post be */}
                <div className="border-t border-cmp-line-2 pt-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Settings className="w-5 h-5 text-cmp-ink" />
                    <h3 className="text-lg font-semibold text-cmp-ink">What will the Post be</h3>
                  </div>
                  <p className="text-sm text-cmp-ink-2 mb-4">
                    Define what type of post to create from the selected content
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-cmp-ink mb-2">Post Type</label>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, postTypeStrategy: 'auto' }))
                        }
                        disabled={!isPostTypeValid('auto', formData.mediaTypes)}
                        className={`flex-1 min-w-[120px] px-4 py-3 rounded flex items-center justify-center gap-2 font-medium transition-colors ${
                          formData.postTypeStrategy === 'auto'
                            ? 'bg-primary text-onfill border border-primary hover:bg-primary-medium'
                            : 'bg-form-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                        } ${!isPostTypeValid('auto', formData.mediaTypes) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Sparkles className="w-5 h-5" />
                        <span>Auto-detect</span>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, postTypeStrategy: 'photo' }))
                        }
                        disabled={!isPostTypeValid('photo', formData.mediaTypes)}
                        className={`flex-1 min-w-[120px] px-4 py-3 rounded flex items-center justify-center gap-2 font-medium transition-colors ${
                          formData.postTypeStrategy === 'photo'
                            ? 'bg-primary text-onfill border border-primary hover:bg-primary-medium'
                            : 'bg-form-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                        } ${!isPostTypeValid('photo', formData.mediaTypes) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Image className="w-5 h-5" />
                        <span>Photo</span>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, postTypeStrategy: 'video' }))
                        }
                        disabled={!isPostTypeValid('video', formData.mediaTypes)}
                        className={`flex-1 min-w-[120px] px-4 py-3 rounded flex items-center justify-center gap-2 font-medium transition-colors ${
                          formData.postTypeStrategy === 'video'
                            ? 'bg-primary text-onfill border border-primary hover:bg-primary-medium'
                            : 'bg-form-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                        } ${!isPostTypeValid('video', formData.mediaTypes) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Video className="w-5 h-5" />
                        <span>Video</span>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, postTypeStrategy: 'post' }))
                        }
                        disabled={!isPostTypeValid('post', formData.mediaTypes)}
                        className={`flex-1 min-w-[120px] px-4 py-3 rounded flex items-center justify-center gap-2 font-medium transition-colors ${
                          formData.postTypeStrategy === 'post'
                            ? 'bg-primary text-onfill border border-primary hover:bg-primary-medium'
                            : 'bg-form-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                        } ${!isPostTypeValid('post', formData.mediaTypes) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Link className="w-5 h-5" />
                        <span>Link Post</span>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, postTypeStrategy: 'random' }))
                        }
                        disabled={!isPostTypeValid('random', formData.mediaTypes)}
                        className={`flex-1 min-w-[120px] px-4 py-3 rounded flex items-center justify-center gap-2 font-medium transition-colors ${
                          formData.postTypeStrategy === 'random'
                            ? 'bg-primary text-onfill border border-primary hover:bg-primary-medium'
                            : 'bg-form-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                        } ${!isPostTypeValid('random', formData.mediaTypes) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Shuffle className="w-5 h-5" />
                        <span>Random</span>
                      </button>
                    </div>
                    {formData.mediaTypes.length > 0 && (
                      <p className="text-xs text-cmp-ink-2 mt-2">
                        {(() => {
                          const allowedTypes = getAllowedPostTypes(formData.mediaTypes);
                          const typeLabels: Record<string, string> = {
                            auto: 'Auto-detect',
                            photo: 'Photo',
                            video: 'Video',
                            post: 'Link Post',
                            random: 'Random',
                          };
                          const allowedLabels = allowedTypes
                            .filter((t) => t !== 'auto' && t !== 'random')
                            .map((t) => typeLabels[t] || t);
                          if (allowedLabels.length > 0) {
                            return `Available post types for selected media: ${allowedLabels.join(', ')}`;
                          }
                          return 'Select media types to see available post types';
                        })()}
                      </p>
                    )}
                  </div>
                </div>

                {/* Schedule Section */}
                <div className="border-t border-cmp-line-2 pt-6 space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-cmp-ink" />
                      <h3 className="text-lg font-semibold text-cmp-ink">When to Post</h3>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="default"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          shifts: [
                            ...prev.shifts,
                            {
                              startTime: '09:00',
                              endTime: '17:00',
                              spansMidnight: false,
                              daysOfWeek: WEEKDAYS,
                            },
                          ],
                        }));
                      }}
                      className="gap-2 py-2 px-4"
                    >
                      <Plus className="w-4 h-4" />
                      Add Shift
                    </Button>
                  </div>

                  <div>
                    <div className="space-y-4">
                      {formData.shifts.map((shift, shiftIndex) => (
                        <div
                          key={shiftIndex}
                          className="p-4 border border-cmp-line-2 rounded-lg bg-form-dark space-y-4"
                        >
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-cmp-ink">
                              Shift {shiftIndex + 1}
                            </h4>
                            {formData.shifts.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    shifts: prev.shifts.filter((_, i) => i !== shiftIndex),
                                  }));
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-cmp-ink mb-1">
                                Start Time
                              </label>
                              <div className="relative">
                                <input
                                  type="time"
                                  value={shift.startTime}
                                  onChange={(e) => {
                                    const newShifts = [...formData.shifts];
                                    newShifts[shiftIndex].startTime = e.target.value;
                                    // Auto-detect if shift spans midnight
                                    const [startHour, startMin] = e.target.value
                                      .split(':')
                                      .map(Number);
                                    const [endHour, endMin] = newShifts[shiftIndex].endTime
                                      .split(':')
                                      .map(Number);
                                    newShifts[shiftIndex].spansMidnight =
                                      endHour < startHour ||
                                      (endHour === startHour && endMin < startMin);
                                    setFormData((prev) => ({ ...prev, shifts: newShifts }));
                                  }}
                                  className="w-full px-3 py-2 pr-10 bg-core-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                                  style={{ colorScheme: 'dark' }}
                                />
                                <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-cmp-ink pointer-events-none" />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-cmp-ink mb-1">
                                End Time
                                {shift.spansMidnight && (
                                  <span className="ml-2 text-primary text-xs">(next day)</span>
                                )}
                              </label>
                              <div className="relative">
                                <input
                                  type="time"
                                  value={shift.endTime}
                                  onChange={(e) => {
                                    const newShifts = [...formData.shifts];
                                    newShifts[shiftIndex].endTime = e.target.value;
                                    // Auto-detect if shift spans midnight
                                    const [startHour, startMin] = newShifts[shiftIndex].startTime
                                      .split(':')
                                      .map(Number);
                                    const [endHour, endMin] = e.target.value.split(':').map(Number);
                                    newShifts[shiftIndex].spansMidnight =
                                      endHour < startHour ||
                                      (endHour === startHour && endMin < startMin);
                                    setFormData((prev) => ({ ...prev, shifts: newShifts }));
                                  }}
                                  className="w-full px-3 py-2 pr-10 bg-core-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                                  style={{ colorScheme: 'dark' }}
                                />
                                <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-cmp-ink pointer-events-none" />
                              </div>
                            </div>
                          </div>

                          {shift.spansMidnight && (
                            <div className="p-2 bg-primary/10 border border-primary/30 rounded text-xs text-primary">
                              ⚠️ This shift spans midnight. It will run from {shift.startTime} until{' '}
                              {shift.endTime} the next day.
                            </div>
                          )}

                          <div>
                            <label className="block text-xs font-medium text-cmp-ink mb-2">
                              Days of Week
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {DAYS_OF_WEEK.map((day) => (
                                <button
                                  key={day.value}
                                  type="button"
                                  onClick={() => {
                                    const newShifts = [...formData.shifts];
                                    const currentDays = newShifts[shiftIndex].daysOfWeek;
                                    newShifts[shiftIndex].daysOfWeek = currentDays.includes(
                                      day.value
                                    )
                                      ? currentDays.filter((d) => d !== day.value)
                                      : [...currentDays, day.value];
                                    setFormData((prev) => ({ ...prev, shifts: newShifts }));
                                  }}
                                  className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                                    shift.daysOfWeek.includes(day.value)
                                      ? 'bg-primary text-onfill'
                                      : 'bg-core-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                                  }`}
                                >
                                  {day.short}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-cmp-ink mb-2">
                      Posting Schedule
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.scheduleType === 'specific_times'}
                          onChange={() =>
                            setFormData((prev) => ({ ...prev, scheduleType: 'specific_times' }))
                          }
                          className="w-4 h-4 accent-cmp-bronze"
                        />
                        <span className="text-cmp-ink">Post at specific times</span>
                      </label>
                      {formData.scheduleType === 'specific_times' && (
                        <div className="ml-6 space-y-3">
                          <div className="space-y-2">
                            {formData.specificTimes.map((time, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="relative">
                                  <input
                                    type="time"
                                    value={time}
                                    onChange={(e) => handleUpdateTime(index, e.target.value)}
                                    className="w-40 px-4 py-2 pr-10 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                                    style={{ colorScheme: 'dark' }}
                                  />
                                  <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-cmp-ink pointer-events-none" />
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveTime(index)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              variant="outline"
                              size="default"
                              onClick={handleAddTime}
                              className="gap-2 py-2 px-4"
                            >
                              <Plus className="w-4 h-4" />
                              Add Time
                            </Button>
                          </div>
                        </div>
                      )}

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.scheduleType === 'quarter_hours'}
                          onChange={() =>
                            setFormData((prev) => ({ ...prev, scheduleType: 'quarter_hours' }))
                          }
                          className="w-4 h-4 accent-cmp-bronze"
                        />
                        <span className="text-cmp-ink">Post at quarter-hour marks</span>
                      </label>
                      {formData.scheduleType === 'quarter_hours' && (
                        <div className="ml-6 space-y-3">
                          <div>
                            <label className="block text-sm text-cmp-ink mb-2">
                              Select which quarter-hour marks to post at:
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {[0, 15, 30, 45].map((mark) => (
                                <button
                                  key={mark}
                                  type="button"
                                  onClick={() => {
                                    setFormData((prev) => {
                                      const marks = prev.quarterHourMarks.includes(mark)
                                        ? prev.quarterHourMarks.filter((m) => m !== mark)
                                        : [...prev.quarterHourMarks, mark];
                                      return {
                                        ...prev,
                                        quarterHourMarks: marks.sort((a, b) => a - b),
                                      };
                                    });
                                  }}
                                  className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                                    formData.quarterHourMarks.includes(mark)
                                      ? 'bg-primary text-onfill'
                                      : 'bg-form-dark text-cmp-ink border border-cmp-line-2 hover:border-cmp-line-2'
                                  }`}
                                >
                                  :{mark.toString().padStart(2, '0')}
                                </button>
                              ))}
                            </div>
                            <p className="text-xs text-cmp-ink-2 mt-2">
                              Posts will be scheduled at the selected marks every hour between your
                              start and end times.
                              <br />
                              Example: Selecting :00 and :30 with 9 AM - 5 PM creates posts at 9:00,
                              9:30, 10:00, 10:30, etc.
                            </p>
                          </div>
                        </div>
                      )}

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.scheduleType === 'interval'}
                          onChange={() =>
                            setFormData((prev) => ({ ...prev, scheduleType: 'interval' }))
                          }
                          className="w-4 h-4 accent-cmp-bronze"
                        />
                        <span className="text-cmp-ink">Post every X hours</span>
                      </label>
                      {formData.scheduleType === 'interval' && (
                        <div className="ml-6 space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="text-cmp-ink">Every</span>
                            <input
                              type="number"
                              min="1"
                              value={formData.intervalMinutes / 60}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  intervalMinutes: parseInt(e.target.value) * 60,
                                }))
                              }
                              className="w-20 px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                            <span className="text-cmp-ink">hours</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Priority Settings */}
                <div className="border-t border-cmp-line-2 pt-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Settings className="w-5 h-5 text-cmp-ink" />
                    <h3 className="text-lg font-semibold text-cmp-ink">Rule Priority</h3>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-cmp-ink mb-2">Priority</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.priority === 'high'}
                          onChange={() => setFormData((prev) => ({ ...prev, priority: 'high' }))}
                          className="w-4 h-4 accent-cmp-bronze"
                        />
                        <span className="text-cmp-ink">High - This rule runs first</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.priority === 'medium'}
                          onChange={() => setFormData((prev) => ({ ...prev, priority: 'medium' }))}
                          className="w-4 h-4 accent-cmp-bronze"
                        />
                        <span className="text-cmp-ink">Medium - Normal priority</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.priority === 'low'}
                          onChange={() => setFormData((prev) => ({ ...prev, priority: 'low' }))}
                          className="w-4 h-4 accent-cmp-bronze"
                        />
                        <span className="text-cmp-ink">Low - This rule runs last</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t border-cmp-line-2">
                  <Button variant="outline" onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateRule}
                    disabled={!formData.name || !formData.socialProfileId}
                  >
                    {editingRule ? 'Save Changes' : 'Create Automation'}
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
